import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { Control } from 'react-hook-form';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { LoginLayout, MAX_WIDTH } from './Layout';
import { LoginFormInputs } from './LoginFormInputs';
import { StayLoggedIn } from './StayLoggedIn';
import { styles } from './styles';
import { isPatientApp } from '@/config/constants';
import { useFeatureFlag } from 'posthog-react-native';
import { FeatureFlag } from '@/contexts/EventsProvider/featureFlags';
import { loginWithEpa } from '@/api/services/epaAuth';
import { EpaAuthContext } from '@/contexts/EpaAuthContext';
import { Divider } from 'react-native-elements';

interface LoginFormData {
  email: string;
  password: string;
  twoFAToken?: string;
  app?: string;
  deviceModel?: string;
  shortLivedSession?: boolean;
  deviceToken?: string;
  loginChannel?: string;
}

interface LoginWrapperProps {
  lastUsedEmail?: string;
  control: Control<LoginFormData>;
  stayLoggedIn: boolean;
  setStayLoggedIn: (value: boolean) => void;
  onPasswordForgotten: () => void;
  onRegister: () => void;
  onLogin: () => void;
  onDemo: () => void;
}

const texts = {
  patient: {
    welcomeNew: 'Willkommen in der neolexon Patienten-App!',
    loginPrompt:
      'Bitte geben Sie hier Ihre neolexon Patienten-Zugangsdaten ein.',
    registerNewUser: 'Neu hier? Jetzt registrieren',
  },
  therapist: {
    welcomeNew: 'Willkommen in der Aphasie-App für Therapeut:innen!',
    loginPrompt: 'Melden Sie sich mit Ihren Zugangsdaten an.',
    registerNewUser: 'Neu hier? Jetzt kostenlos registrieren',
  },
};

const getTexts = (isPatient: boolean) =>
  texts[isPatient ? 'patient' : 'therapist'];

export const LoginWrapper: React.FC<LoginWrapperProps> = ({
  lastUsedEmail,
  control,
  stayLoggedIn,
  setStayLoggedIn,
  onPasswordForgotten,
  onRegister,
  onLogin,
  onDemo,
}) => {
  const navigation = useNavigation();
  const authContext = useContext(EpaAuthContext);
  const [epaError, setEpaError] = useState<string>();
  const currentTexts = getTexts(isPatientApp);
  const epaEnabled = useFeatureFlag(FeatureFlag.EPA_ENABLED);

  useLayoutEffect(() => {
    if (!lastUsedEmail) {
      navigation.setOptions({ title: '' });
    }
  }, []);

  const LoginForm = () => {
    return (
      <>
        <Text style={{ marginBottom: 16 }}>{currentTexts.loginPrompt}</Text>
        <LoginFormInputs control={control} />
        <View style={{ alignItems: 'flex-end' }}>
          <Button
            label="Passwort vergessen?"
            onPress={onPasswordForgotten}
            look="tertiary"
          />
        </View>
        <StayLoggedIn checked={stayLoggedIn} onToggle={setStayLoggedIn} />
      </>
    );
  };

  const renderReturningUserContent = () => {
    return (
      <LoginLayout
        scrollableHeader={
          <Text style={styles.therapistWelcomeText}>👋 Willkommen zurück!</Text>
        }
        outerButtons={
          <>
            <View style={styles.therapistMultipleButtonsContainer}>
              <Button
                label="Registrieren"
                look="tertiary"
                onPress={onRegister}
                testID="login-register"
              />
              <Button
                label="Anmelden"
                look="primary"
                onPress={onLogin}
                testID="login-submit"
              />
            </View>
            {renderEpa()}
          </>
        }
      >
        <LoginForm />
      </LoginLayout>
    );
  };

  const renderNewUserContent = () => {
    return (
      <LoginLayout
        scrollableHeader={
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            <View style={{ width: MAX_WIDTH }}>
              <Text style={styles.therapistWelcomeText}>
                {currentTexts.welcomeNew}
              </Text>
              <View
                style={{
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  marginBottom: 24,
                }}
              >
                <Button
                  label="Demo ansehen "
                  look="secondary"
                  onPress={onDemo}
                  testID="login-demo"
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    marginRight: 8,
                  }}
                />
                <Button
                  label={currentTexts.registerNewUser}
                  look="primary"
                  onPress={onRegister}
                  testID="login-register"
                  style={{ flex: 1, justifyContent: 'center' }}
                />
              </View>
            </View>
          </View>
        }
      >
        <LoginForm />
        <View style={styles.therapistSingleButtonContainer}>
          <Button
            label="Anmelden"
            look="primary"
            onPress={onLogin}
            testID="login-submit"
          />
        </View>
        {renderEpa()}
      </LoginLayout>
    );
  };

  const registerWithInsuranceId = useCallback(async () => {
    await loginWithEpa(authContext, () => {
      setEpaError(
        'Es wurde kein Account gefunden, der mit diesen Krankenkassendaten verknüpft ist. Bitte melden Sie sich in Ihrem bestehenden Neolexon-Account an und verknüpfen Sie diesen mit der ePA (elektronische Patientenaktie).',
      );
    });
  }, [authContext]);

  const renderEpa = useCallback(() => {
    if (epaEnabled && isPatientApp) {
      return (
        <>
          <View style={styles.epaDividerContainer}>
            <Divider color={colors.mediumGrey} style={{ flex: 1 }} width={1} />
            <Text style={styles.epaDividerText}>oder</Text>
            <Divider color={colors.mediumGrey} style={{ flex: 1 }} width={1} />
          </View>
          <Button
            label="Mit GesundheitsID anmelden"
            look="secondary"
            style={{ justifyContent: 'center' }}
            onPress={registerWithInsuranceId}
          ></Button>
          {!!epaError && <Text style={styles.epaErrorText}>{epaError}</Text>}
        </>
      );
    }
  }, [authContext, epaError, epaEnabled]);

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.newUI.therapist.blue20,
      }}
    >
      {lastUsedEmail ? renderReturningUserContent() : renderNewUserContent()}
    </View>
  );
};
